import { Component, ChangeDetectionStrategy, effect, inject, input, model, output } from '@angular/core';
import { FormsModule } from '@angular/forms';

import { ButtonModule } from '@openng/optimus-ui/button';
import { ChipModule } from '@openng/optimus-ui/chip';
import { DialogModule } from '@openng/optimus-ui/dialog';
import { InputTextModule } from '@openng/optimus-ui/inputtext';
import { SelectModule } from '@openng/optimus-ui/select';

import { RoleOption, SystemRole, TeamUserService } from '../../../core/services/team-user.service';

export interface BulkInviteSubmit {
  emails: string[];
  systemRole: SystemRole;
  position: string;
}

/** "Bulk Invite Employees" dialog; owns the email chips with server-side validation. */
@Component({
  selector: 'app-user-bulk-invite-modal',
  imports: [FormsModule, ButtonModule, ChipModule, DialogModule, InputTextModule, SelectModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <p-dialog
      header="Bulk Invite Employees"
      [(visible)]="visible"
      [modal]="true"
      [closable]="true"
      [draggable]="false"
      [resizable]="false"
      [baseZIndex]="1000000"
      styleClass="w-full max-w-md">
      <form (ngSubmit)="submit()" class="flex flex-col gap-5">
        <div class="flex flex-col gap-2">
          <label for="emails" class="font-semibold">Email Addresses <span class="text-red-500">*</span></label>

          <!-- Email chips display -->
          @if (emailChips.length > 0) {
            <div class="flex flex-wrap gap-2 mb-2">
              @for (email of emailChips; track email) {
                <p-chip
                  [label]="email"
                  [removable]="true"
                  (onRemove)="removeEmailChip(email)"
                  class="shadow-sm">
                  <i class="pi pi-check text-green-500 mr-1"></i>
                </p-chip>
              }
            </div>
          }

          <!-- Email input -->
          <input
            pInputText
            id="emails"
            [(ngModel)]="currentEmailInput"
            name="emails"
            placeholder="Enter email addresses here... "
            [class.ng-invalid]="!!formErrors['emails']"
            [class.ng-dirty]="!!formErrors['emails']"
            class="w-full h-24 text-base"
            (keydown)="onEmailKeydown($event)"
            (blur)="onEmailInputBlur()" />

          @if (formErrors['emails']) {
            <small class="p-error">{{ formErrors['emails'] }}</small>
          }
          <small class="text-gray-500">Type email and press Enter or comma to add. Click × to remove.</small>
        </div>

        <div class="flex flex-col gap-2">
          <label for="bulkSystemRole" class="font-semibold">Default Role</label>
          <p-select
            [options]="roleOptions()"
            [(ngModel)]="systemRole"
            name="bulkSystemRole"
            placeholder="Select a default role"
            optionLabel="label"
            optionValue="value"
            styleClass="w-full"
            [appendTo]="'body'">
          </p-select>
        </div>

        <div class="flex flex-col gap-2">
          <label for="bulkPosition" class="font-semibold">Default Position</label>
          <input
            pInputText
            id="bulkPosition"
            [(ngModel)]="position"
            name="bulkPosition"
            placeholder="Enter default position"
            class="w-full" />
        </div>

        <div class="flex justify-end gap-2 pt-4">
          <p-button label="Cancel" severity="secondary" (onClick)="visible.set(false)" type="button"></p-button>
          <p-button
            [label]="'Send Invitations' + (emailChips.length > 0 ? ' (' + emailChips.length + ')' : '')"
            [loading]="loading()"
            type="submit"
            [disabled]="emailChips.length === 0 || currentEmailInput.trim() !== ''"
            icon="pi pi-send">
          </p-button>
        </div>
      </form>
    </p-dialog>
  `
})
export class UserBulkInviteModalComponent {
  private readonly teamUserService = inject(TeamUserService);

  readonly visible = model(false);
  readonly roleOptions = input<RoleOption[]>([]);
  readonly loading = input(false);

  readonly send = output<BulkInviteSubmit>();

  systemRole: SystemRole = SystemRole.Designer;
  position = '';
  emailChips: string[] = [];
  currentEmailInput = '';
  formErrors: Record<string, string> = {};

  // Closing the dialog clears the draft, mirroring the previous closeModals cleanup.
  private readonly resetOnClose = effect(() => {
    if (!this.visible()) {
      this.systemRole = SystemRole.Designer;
      this.position = '';
      this.emailChips = [];
      this.currentEmailInput = '';
      this.formErrors = {};
    }
  });

  onEmailKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      this.addCurrentEmail();
    }
  }

  addCurrentEmail(): void {
    const email = this.currentEmailInput.trim();
    if (email && isValidEmail(email) && !this.emailChips.includes(email)) {
      // Validate email before adding
      this.teamUserService.validateEmailForInvitation(email).subscribe({
        next: (result) => {
          if (result.canInvite) {
            this.emailChips.push(email);
            this.currentEmailInput = '';
            this.clearEmailError();
          } else {
            this.formErrors['emails'] = result.message;
          }
        },
        error: () => {
          // If validation fails, still allow adding but show warning
          this.emailChips.push(email);
          this.currentEmailInput = '';
          this.clearEmailError();
        }
      });
    } else if (email && !isValidEmail(email)) {
      this.formErrors['emails'] = 'Please enter a valid email address';
    } else if (email && this.emailChips.includes(email)) {
      this.formErrors['emails'] = 'This email is already added';
    }
  }

  removeEmailChip(email: string): void {
    const index = this.emailChips.indexOf(email);
    if (index > -1) {
      this.emailChips.splice(index, 1);
      this.clearEmailError();
    }
  }

  onEmailInputBlur(): void {
    if (this.currentEmailInput.trim()) {
      this.addCurrentEmail();
    }
  }

  submit(): void {
    this.send.emit({ emails: [...this.emailChips], systemRole: this.systemRole, position: this.position });
  }

  private clearEmailError(): void {
    if (this.formErrors['emails']) {
      delete this.formErrors['emails'];
    }
  }
}

function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}
